import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { TextInput, NumberInput, Services, Modal, PhoneInput, BubbleSelectBlock, Text, i18n, tokens } from '@simplybenefits/sb-ui-library';
import FormControl from '@mui/material/FormControl';
import { editAccount } from '../../actions/accounts.js';
import permissionLevel from '../../services/permissions.js';

const statusMapString = {
    0: 'In Progress',
    1: 'Active',
    3: 'Under Revisions',
    2: 'Disabled',
    9: 'Cancelled'
};

const statusMapInt = {
    'In Progress': 0,
    Active: 1,
    Disabled: 2,
    'Under Revisions': 3,
    Cancelled: 9
};

class Settings extends PureComponent {
    state = {
        address: '',
        companyName: '',
        addressLineTwo: '',
        addressLineOne: '',
        city: '',
        province: 'Alberta',
        postalCode: '',
        country: 'Canada',
        contactEmail: '',
        phone: '',
        status: '',
        authorityName: '',
        authorityEmail: '',
        isASO: false,
        asoType: null,
        isAsoDental: null,
        isAsoHealth: null,
        asoRunoffPeriod: null,
        secondaryPolicyNumber: null,
        oocPolicyNumber: null,
        allowVoluntaryBenefits: 1,
        allowOptionalBenefits: 0,
        spendingAccountFloatExempt: 0,
        experienceRatedRateGuarantee: null,
        pooledRateGuarantee: null,
        underwritingPeriod: null,
        enforceMFA: false
    };

    componentDidMount() {
        const { meta } = this.props;

        this.setState({
            addressLineTwo: meta.addressLineTwo || '',
            addressLineOne: meta.addressLineOne || '',
            city: meta.city || '',
            province: meta.province || '',
            postalCode: meta.postalCode || '',
            companyName: meta.company,
            contactEmail: meta.contactEmail,
            phone: meta.phone,
            status: statusMapString[!isNaN(meta.status) ? meta.status : 2],
            authorityName: meta.authorityName,
            authorityEmail: meta.authorityEmail,
            isASO: meta.isASO,
            asoType: meta.asoType,
            isAsoDental: meta.isAsoDental,
            isAsoHealth: meta.isAsoHealth,
            asoRunoffPeriod: meta.asoRunoffPeriod,
            secondaryPolicyNumber: meta.secondaryPolicyNumber || null,
            oocPolicyNumber: meta.oocPolicyNumber || null,
            allowVoluntaryBenefits: meta.allowVoluntaryBenefits === undefined ? 1 : meta.allowVoluntaryBenefits,
            allowOptionalBenefits: meta.allowOptionalBenefits || 0,
            spendingAccountFloatExempt: meta.spendingAccountFloatExempt || 0,
            experienceRatedRateGuarantee: meta.experienceRatedRateGuarantee,
            pooledRateGuarantee: meta.pooledRateGuarantee,
            underwritingPeriod: meta.underwritingPeriod,
            enforceMFA: meta.enforceMFA
        });
    }

    componentWillReceiveProps() {
        if (!permissionLevel('Account Settings', this.props.accessType).includes('read')) {
            window.location.href = '/403';
        }

        return null;
    }

    _onBlur = async (type) => {
        this.setState({
            [`${ type }Error`]: await Services.validateInput(type, this.state[type])
        });
    };

    _handleInputChange = (type, event, isValue = false) => {
        this.setState({
            [type]: isValue ? event : event.target.value
        });
    };

    _validateForm = async () => {
        const statusError = await Services.validateInput('status', this.state.status);
        const companyNameError = await Services.validateInput('companyName', this.state.companyName);
        const addressLineOneError = await Services.validateInput('addressLineOne', this.state.addressLineOne);
        const cityError = await Services.validateInput('city', this.state.city);
        const provinceError = await Services.validateInput('province', this.state.province);
        const postalCodeError = await Services.validateInput('postalCode', this.state.postalCode);
        const phoneError = await Services.validateInput('phone', this.state.phone);
        const contactEmailError = await Services.validateInput('contactEmail', this.state.contactEmail);
        const authorityError = await Services.validateInput('authority', this.state.authorityName);
        const authorityEmailError = await Services.validateInput('authorityEmail', this.state.authorityEmail);
        const experienceRatedRateGuaranteeError = await Services.validateInput('checkEmpty', this.state.experienceRatedRateGuarantee);
        const pooledRateGuaranteeError = await Services.validateInput('checkEmpty', this.state.pooledRateGuarantee);
        const underwritingPeriodError = await Services.validateInput('checkEmpty', this.state.underwritingPeriod);

        this.setState({
            companyNameError,
            addressLineOneError,
            cityError,
            provinceError,
            postalCodeError,
            phoneError,
            contactEmailError,
            authorityError,
            authorityEmailError,
            statusError,
            experienceRatedRateGuaranteeError,
            pooledRateGuaranteeError,
            underwritingPeriodError
        });

        if (
            companyNameError ||
            phoneError ||
            contactEmailError ||
            authorityError ||
            authorityEmailError ||
            statusError ||
            addressLineOneError ||
            cityError ||
            provinceError ||
            postalCodeError ||
            experienceRatedRateGuaranteeError ||
            pooledRateGuaranteeError ||
            underwritingPeriodError
        ) {
            return false;
        }

        return true;
    };

    _saveAccount = async () => {
        const isFormValid = await this._validateForm();

        if (!isFormValid) {
            return false;
        }

        const {
            companyName,
            contactEmail,
            phone,
            status,
            authorityName,
            authorityEmail,
            addressLineTwo,
            addressLineOne,
            city,
            province,
            postalCode,
            secondaryPolicyNumber,
            oocPolicyNumber,
            experienceRatedRateGuarantee,
            pooledRateGuarantee,
            underwritingPeriod,
            enforceMFA
        } = this.state;

        const body = {
            company: companyName,
            status: statusMapInt[status],
            contactEmail,
            addressLineOne,
            addressLineTwo: addressLineTwo || null,
            city,
            province,
            postalCode,
            phone,
            authorityName,
            authorityEmail,
            enforceMFA: Boolean(enforceMFA),
            allowVoluntaryBenefits: Boolean(this.state.allowVoluntaryBenefits),
            allowOptionalBenefits: Boolean(this.state.allowOptionalBenefits),
            spendingAccountFloatExempt: Boolean(this.state.spendingAccountFloatExempt),
            experienceRatedRateGuarantee: Number(experienceRatedRateGuarantee),
            pooledRateGuarantee: Number(pooledRateGuarantee),
            underwritingPeriod: Number(underwritingPeriod),
            oocPolicyNumber: oocPolicyNumber || null,
            secondaryPolicyNumber: secondaryPolicyNumber || null
        };

        this.props.dispatch(editAccount(this.props.meta.accountID, body));

        return true;
    };

    /**
     * 
     * @param {*} input 
     */
    _formatPostalCode = async (input) => {
        const formattedPostalCode = input
            .toUpperCase()
            .trim()
            .replace(/\s+/g, '') // Remove all spaces
            .replace(/([A-Z0-9]{3})([A-Z0-9]{3})/, '$1 $2'); // Add a space after the first 3 characters


        await this._handleInputChange('postalCode', formattedPostalCode, true);
        this._onBlur('postalCode');
    };

    render() {
        const { hideModal } = this.props;
        const writeAccess = permissionLevel('Account Settings', this.props.accessType).includes('write');
        const hasSpendingAccounts = (this.props.plan.spendingAccounts || []).some((classData) => classData.coverage.isSelected);

        const styles = {
            row: {
                display: 'flex',
                flexDirection: 'row'
            },
            divider: {
                width: 40
            }
        };

        return (
            <Modal
                medium
                testid="account-settings-modal"
                open={true}
                onClose={hideModal}
                title="Modify Account Details"
                primaryButton={{
                    onClick: writeAccess ? this._saveAccount : false
                }}
                loader={this.props.loader}
            >
                <TextInput
                    label="Company Name"
                    value={this.state.companyName}
                    onBlur={() => this._onBlur('companyName')}
                    onChange={(value) => this._handleInputChange('companyName', value)}
                    error={Boolean(this.state.companyNameError)}
                    helperText={this.state.companyNameError}
                    fullWidth
                    autofocus
                />

                <TextInput
                    label="Address Line One"
                    autocomplete="shipping street-address"
                    value={this.state.addressLineOne}
                    onBlur={() => this._onBlur('addressLineOne')}
                    onChange={(value) => this._handleInputChange('addressLineOne', value)}
                    error={Boolean(this.state.addressLineOneError)}
                    helperText={this.state.addressLineOneError}
                    fullWidth
                />

                <div style={styles.row}>
                    <TextInput
                        label="Address Line Two"
                        value={this.state.addressLineTwo}
                        onBlur={() => this._onBlur('addressLineTwo')}
                        onChange={(value) => this._handleInputChange('addressLineTwo', value)}
                        fullWidth
                        helperText={i18n.t(tokens.labels.addressLineTwoExample) }
                    />

                    <div style={styles.divider} />

                    <TextInput
                        label="City/Town"
                        value={this.state.city}
                        onBlur={() => this._onBlur('city')}
                        onChange={(value) => this._handleInputChange('city', value)}
                        error={Boolean(this.state.cityError)}
                        helperText={this.state.cityError}
                        fullWidth
                    />
                </div>

                <div style={styles.row}>
                    <TextInput
                        label="Province"
                        value={this.state.province}
                        onBlur={() => this._onBlur('province')}
                        onChange={(value) => this._handleInputChange('province', value)}
                        select
                        selectOptions={Services.selectFieldOptions.provinceOptions()}
                        fullWidth
                    />

                    <div style={styles.divider} />

                    <TextInput
                        label="Postal Code"
                        value={this.state.postalCode}
                        onBlur={(e) => this._formatPostalCode(e.target.value)}
                        onChange={(e) => {

                            // Allow typing only 6 alphanumeric characters and trim any excess
                            const postalCodeValue = e.target.value
                                .toUpperCase()
                                .slice(0, 7);
                            this._handleInputChange('postalCode', postalCodeValue, true);
                        }}
                        error={Boolean(this.state.postalCodeError)}
                        helperText={this.state.postalCodeError}
                        fullWidth
                    />
                </div>

                <PhoneInput
                    label="Company Contact Number"
                    value={this.state.phone}
                    onBlur={() => this._onBlur('phone')}
                    onChange={(value) => this._handleInputChange('phone', value)}
                    error={Boolean(this.state.phoneError)}
                    helperText={this.state.phoneError}
                    fullWidth
                />

                <TextInput
                    label="Company Email"
                    value={this.state.contactEmail}
                    onBlur={() => this._onBlur('contactEmail')}
                    onChange={(value) => this._handleInputChange('contactEmail', value)}
                    error={Boolean(this.state.contactEmailError)}
                    helperText={this.state.contactEmailError}
                    fullWidth
                />

                <TextInput
                    label="Signing Authority Name"
                    value={this.state.authorityName}
                    onBlur={() => this._onBlur('authorityName')}
                    onChange={(value) => this._handleInputChange('authorityName', value)}
                    error={Boolean(this.state.authorityError)}
                    helperText={this.state.authorityError}
                    fullWidth
                />

                <TextInput
                    label="Signing Authority Email"
                    value={this.state.authorityEmail}
                    onBlur={() => this._onBlur('authorityEmail')}
                    onChange={(value) => this._handleInputChange('authorityEmail', value)}
                    error={Boolean(this.state.authorityEmailError)}
                    helperText={this.state.authorityEmailError}
                    fullWidth
                />

                <TextInput
                    label="Status"
                    value={this.state.status}
                    onBlur={() => this._onBlur('status')}
                    onChange={(value) => this._handleInputChange('status', value)}
                    error={Boolean(this.state.statusError)}
                    helperText={this.state.statusError}
                    fullWidth
                    disabled={ this.state.status === 'Under Revisions' }
                    select
                    selectOptions={[
                        {
                            key: '0',
                            value: 'In Progress'
                        },
                        {
                            key: '1',
                            value: 'Active'
                        },
                        {
                            key: '2',
                            value: 'Disabled'
                        },
                        {
                            key: '3',
                            value: 'Under Revisions'
                        },
                        {
                            key: '9',
                            value: 'Cancelled'
                        }
                    ]}
                />

                <FormControl component="fieldset">
                    <Text
                        medium
                        light
                        margin="20px 0 0px 0"
                    >
                        Enforce Multi-factor Authentication (MFA)
                    </Text>

                    <Text
                        tiny
                        light
                        margin="5px 0 5px 0"
                    >
                        Enforce Admins and Members to use multi-factor authentication when attempting login.
                    </Text>

                    <BubbleSelectBlock
                        first={{
                            title: 'Yes',
                            selected: Boolean(this.state.enforceMFA) === true,
                            field: 'enforceMFAYes'
                        }}
                        second={{
                            title: 'No',
                            selected: Boolean(this.state.enforceMFA) === false,
                            field: 'enforceMFANo'
                        }}
                        onBlur={(e) => this._onBlur('enforceMFA', e)}
                        handleChange={ async (field) => {
                            if (field === 'enforceMFAYes') {
                                await this._handleInputChange('enforceMFA', true, true);
                            } else {
                                await this._handleInputChange('enforceMFA', false, true);
                            }
                        }}
                    />
                </FormControl>

                {
                    !this.props.currentAccountMeta.isPrivateLabel &&
                    <>
                        <FormControl component="fieldset">
                            <Text
                                medium
                                light
                                margin="20px 0 0px 0"
                            >
                                Include Optional Benefits
                            </Text>

                            <Text
                                tiny
                                light
                                margin="5px 0 5px 0"
                            >
                                Even if Optional Benefits is disabled, you will still be able to add them via the Master Site. However, members will not be able to purchase them directly from the Member portal or app.
                            </Text>

                            <BubbleSelectBlock
                                disabled={ true }
                                first={{
                                    title: 'Yes',
                                    selected: Boolean(this.state.allowOptionalBenefits) === true,
                                    field: 'allowOptionalBenefitsYes'
                                }}
                                second={{
                                    title: 'No',
                                    selected: Boolean(this.state.allowOptionalBenefits) === false,
                                    field: 'allowOptionalBenefitsNo'
                                }}
                                onBlur={(e) => this._onBlur('allowOptionalBenefits', e)}
                                handleChange={ async (field) => {
                                    if (field === 'allowOptionalBenefitsYes') {
                                        await this._handleInputChange('allowOptionalBenefits', true, true);
                                    } else {
                                        await this._handleInputChange('allowOptionalBenefits', false, true);
                                    }
                                }}
                            />
                        </FormControl>

                        <FormControl component="fieldset">
                            <Text
                                medium
                                light
                                margin="20px 0 5px 0"
                            >
                                Include Voluntary Benefits
                            </Text>

                            <BubbleSelectBlock
                                first={{
                                    title: 'Yes',
                                    selected: Boolean(this.state.allowVoluntaryBenefits) === true,
                                    field: 'allowVoluntaryBenefitsYes'
                                }}
                                second={{
                                    title: 'No',
                                    selected: Boolean(this.state.allowVoluntaryBenefits) === false,
                                    field: 'allowVoluntaryBenefitsNo'
                                }}
                                onBlur={(e) => this._onBlur('allowVoluntaryBenefits', e)}
                                handleChange={ async (field) => {
                                    if (field === 'allowVoluntaryBenefitsYes') {
                                        await this._handleInputChange('allowVoluntaryBenefits', true, true);
                                    } else {
                                        await this._handleInputChange('allowVoluntaryBenefits', false, true);
                                    }
                                }}
                            />
                        </FormControl>

                        {
                            hasSpendingAccounts ?
                                <>
                                    <FormControl component="fieldset">
                                        <Text
                                            medium
                                            light
                                            margin="20px 0 5px 0"
                                        >
                                            Spending Account Floats
                                        </Text>

                                        <Text
                                            tiny
                                            light
                                            margin="5px 0 5px 0"
                                        >
                                            Select 'Standard' to apply default Spending Account float rules. Select 'Exempt' to exclude this account from Spending Account float requirements. These options are only relevant if Spending Accounts are active.
                                        </Text>

                                        <BubbleSelectBlock
                                            first={{
                                                title: 'Standard',
                                                selected: Boolean(this.state.spendingAccountFloatExempt) === false,
                                                field: 'standardFloat'
                                            }}
                                            second={{
                                                title: 'Exempt',
                                                selected: Boolean(this.state.spendingAccountFloatExempt) === true,
                                                field: 'exemptFloat'
                                            }}
                                            onBlur={(e) => this._onBlur('spendingAccountFloatExempt', e)}
                                            handleChange={ (field) => {
                                                const isExempt = field === 'exemptFloat';
                                                this._handleInputChange('spendingAccountFloatExempt', isExempt, true);
                                            }}
                                        />
                                    </FormControl>
                                </>
                                :
                                null
                        }

                        <Text
                            medium
                            light
                            margin="20px 0 0px 0"
                        >
                            Rate Guarantees
                        </Text>

                        <TextInput
                            label="Experience Rated Benefits Rate Guarantee"
                            value={this.state.experienceRatedRateGuarantee}
                            onBlur={() => this._onBlur('experienceRatedRateGuarantee')}
                            onChange={(value) => this._handleInputChange('experienceRatedRateGuarantee', value)}
                            error={Boolean(this.state.experienceRatedRateGuaranteeError)}
                            helperText={this.state.experienceRatedRateGuaranteeError}
                            disabled={this.props.meta.status !== 0} // Can only edit before going live
                            fullWidth
                        />

                        <TextInput
                            label="Pooled Benefits Rate Guarantee"
                            value={this.state.pooledRateGuarantee}
                            onBlur={() => this._onBlur('pooledRateGuarantee')}
                            onChange={(value) => this._handleInputChange('pooledRateGuarantee', value)}
                            error={Boolean(this.state.pooledRateGuaranteeError)}
                            helperText={this.state.pooledRateGuaranteeError}
                            disabled={this.props.meta.status !== 0} // Can only edit before going live
                            fullWidth
                        />

                        <TextInput
                            label="Underwriting Period"
                            value={this.state.underwritingPeriod}
                            onBlur={() => this._onBlur('underwritingPeriod')}
                            onChange={(value) => this._handleInputChange('underwritingPeriod', value)}
                            error={Boolean(this.state.underwritingPeriodError)}
                            helperText={this.state.underwritingPeriodError}
                            disabled={this.props.meta.status !== 0} // Can only edit before going live
                            fullWidth
                        />

                        <Text
                            medium
                            light
                            margin="20px 0 0px 0"
                        >
                            Additional Policy Numbers
                        </Text>

                        <TextInput
                            label="Secondary Policy Number"
                            value={this.state.secondaryPolicyNumber || ''}
                            onChange={(value) => this._handleInputChange('secondaryPolicyNumber', value)}
                            fullWidth
                        />

                        <TextInput
                            label="OOC Policy Number"
                            value={this.state.oocPolicyNumber || ''}
                            onChange={(value) => this._handleInputChange('oocPolicyNumber', value)}
                            fullWidth
                        />

                        <Text
                            medium
                            light
                            margin="10px 0 10px 0"
                        >
                            Please contact the product team to make any changes to this account's ASO settings.
                        </Text>

                        <NumberInput
                            label={ 'ASO Run-Off Period Duration (days)' }
                            value={ this.state.asoRunoffPeriod }
                            customStyle={{ width: '50%' }}
                            disabled={ true }
                        />

                        <FormControl component="fieldset">
                            <Text
                                small
                                bold
                                margin="10px 0 0 0"
                            >
                                ASO Type
                            </Text>

                            <BubbleSelectBlock
                                first={{
                                    title: 'Budgeted',
                                    selected: this.state.asoType === 'Budgeted',
                                    field: 'Budgeted'
                                }}
                                second={{
                                    title: 'In Arrears',
                                    selected: this.state.asoType === 'Arrears',
                                    field: 'Arrears'
                                }}
                                disabled={ true }
                            />
                        </FormControl>

                        <FormControl component="fieldset">
                            <Text
                                medium
                                bold
                                margin="10px 0 10px 0"
                            >
                                ASO Dental
                            </Text>

                            <BubbleSelectBlock
                                first={{
                                    title: 'Yes',
                                    selected: Boolean(this.state.isAsoDental) === true,
                                    field: 'isAsoDentalYes'
                                }}
                                second={{
                                    title: 'No',
                                    selected: Boolean(this.state.isAsoDental) === false,
                                    field: 'isAsoDentalNo'
                                }}
                                disabled={ true }
                            />
                        </FormControl>

                        <FormControl component="fieldset">
                            <Text
                                medium
                                bold
                                margin="10px 0 10px 0"
                            >
                                ASO Health
                            </Text>

                            <BubbleSelectBlock
                                first={{
                                    title: 'Yes',
                                    selected: Boolean(this.state.isAsoHealth) === true,
                                    field: 'isAsoHealthYes'
                                }}
                                second={{
                                    title: 'No',
                                    selected: Boolean(this.state.isAsoHealth) === false,
                                    field: 'isAsoHealthNo'
                                }}
                                disabled={ true }
                            />
                        </FormControl>
                    </>
                }
            </Modal>
        );
    }
}

Settings.propTypes = {
    meta: PropTypes.object
};

Settings.propTypes = {
    loader: PropTypes.bool,
    meta: PropTypes.object,
    accessType: PropTypes.string
};

/**
 *
 * @param {*} state
 */
const mapStateToProps = (state) => ({
    loader: state.loader,
    meta: state.accounts.currentAccount.meta,
    accessType: state.master.access,
    plan: state.accounts.currentAccount.plan
});

export default connect(mapStateToProps)(Settings);